/**
 * Custom Firebase Auth action handler — /password-reset/confirm.
 *
 * Firebase points every auth email link at one project-wide action URL. This
 * page owns `mode=resetPassword` (so people set their new password on
 * hiiker.app rather than the firebaseapp.com page) and forwards every other
 * mode straight back to Firebase's own handler untouched — see
 * utils/firebase/action_code.ts for why that forwarding rule matters.
 *
 * No-ops on pages without the mount point, so it is safe in the shared
 * `users` pack.
 */
import { auth, verifyPasswordResetCode, confirmPasswordReset } from '../../utils/firebase/auth';
import { friendlyAuthError } from '../../utils/firebase/auth_errors';
import { resolveActionRoute, safeContinueUrl, validateNewPassword } from '../../utils/firebase/action_code';
import { i18n } from '../../i18n';

const root = document.getElementById('password-reset-confirm');

if (root) {
  const el = (id: string) => document.getElementById(id);

  const loadingState = el('prc-loading');
  const formState = el('prc-form');
  const invalidState = el('prc-invalid');
  const successState = el('prc-success');
  const emailLabel = el('prc-email');
  const passwordInput = el('prc-password') as HTMLInputElement | null;
  const confirmationInput = el('prc-password-confirmation') as HTMLInputElement | null;
  const submitButton = el('prc-submit') as HTMLButtonElement | null;
  const errorBox = el('prc-error');
  const errorText = el('prc-error-text');
  const invalidText = el('prc-invalid-text');

  const show = (node: HTMLElement | null) => { if (node) node.style.display = 'block'; };
  const hide = (node: HTMLElement | null) => { if (node) node.style.display = 'none'; };

  const showError = (message: string) => {
    if (errorText) errorText.textContent = message;
    show(errorBox);
  };

  const showInvalid = (message: string) => {
    hide(loadingState);
    hide(formState);
    if (invalidText) invalidText.textContent = message;
    show(invalidState);
  };

  const authDomain = (window as any).firebaseConfigs?.authDomain as string | undefined;
  const route = resolveActionRoute(window.location.search, authDomain || '');

  if (route.kind === 'forward') {
    // Not our mode (magic-link sign-in, email verification, ...). Hand back to
    // Firebase without touching the query string. `replace` keeps this page out
    // of history so Back doesn't bounce the user into a used code.
    window.location.replace(route.url);
  } else if (route.kind === 'invalid') {
    showInvalid(i18n.global.t('password_reset_confirm.main.link_invalid_or_incomplete'));
  } else {
    const { oobCode, continueUrl } = route;

    // Validate the code before showing the form, so an expired link says so up
    // front instead of after the user has typed a new password twice.
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        if (emailLabel) emailLabel.textContent = email;
        hide(loadingState);
        show(formState);
        passwordInput?.focus();
      })
      .catch((error) => {
        showInvalid(friendlyAuthError(error, i18n.global.t('password_reset_confirm.main.link_invalid_or_expired')));
      });

    const submit = async () => {
      hide(errorBox);

      const password = passwordInput?.value ?? '';
      const confirmation = confirmationInput?.value ?? '';
      const validation = validateNewPassword(password, confirmation);
      if (!validation.valid) {
        showError(validation.error as string);
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = i18n.global.t('common.saving');
      }

      try {
        await confirmPasswordReset(auth, oobCode, password);
        hide(formState);
        show(successState);

        // Only ever follow a continueUrl on our own origin — it comes from the
        // query string, so honouring it blindly would be an open redirect.
        const destination = safeContinueUrl(continueUrl, window.location.origin) || '/login';
        window.setTimeout(() => { window.location.assign(destination); }, 2000);
      } catch (error) {
        showError(friendlyAuthError(error, i18n.global.t('password_reset_confirm.main.could_not_reset')));
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = i18n.global.t('users.password_reset_confirm.set_new_password');
        }
      }
    };

    submitButton?.addEventListener('click', (e) => {
      e.preventDefault();
      submit();
    });

    [passwordInput, confirmationInput].forEach((input) => {
      input?.addEventListener('keypress', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') {
          e.preventDefault();
          submit();
        }
      });
    });
  }
}
